import React from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { ChevronDown, Plus, FolderPlus } from 'lucide-react';
import { Dropdown } from 'components/molecules/dropdown';
import { Card } from 'components/atoms/Card';
import FolderNameModal from 'components/organisms/folderTree/FolderNameModal';
import { Link } from 'react-router-dom';
import ListsNavTree from '../../organisms/folderTree/listsNavTree';
import { useListLinks } from './hooks/useListLinks';

export const ListLinks = ({ slug }) => {
  const {
    isOpen,
    creatingFolder,
    openCreateFolder,
    handleToggleSection,
    handleCreateListClick,
    openCreateFolderModal,
    handleCancelCreateFolder,
    handleSubmitCreateFolder,
  } = useListLinks();

  return (
    <Box sx={{ flex: '1 1 auto', minHeight: 0 }}>
      <Flex sx={{ gap: '8px', pl: '8px' }} alignItems="center">
        <Flex sx={{ gap: '4px', alignItems: 'center', cursor: 'pointer' }} onClick={handleToggleSection}>
          <Box
            sx={{
              fontSize: '12px',
              fontWeight: '500',
              color: 'text.subtle',
              lineHeight: '16px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            }}
          >
            Lists
          </Box>
          <Box
            as={ChevronDown}
            size="16px"
            aria-hidden="true"
            flex="0 0 auto"
            sx={{
              color: 'neutral.400',
              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.1s ease-in-out',
            }}
          />
        </Flex>
        <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 'auto' }}>
          <Dropdown
            placement="right-start"
            trigger={
              <Flex
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bg: 'purple.400',
                    color: 'neutral.0',
                  },
                }}
              >
                <Box as={Plus} size="16px" sx={{ transition: 'all 0.3s ease' }} />
              </Flex>
            }
          >
            <Card sx={{ cursor: 'pointer', width: '228px', gap: '4px' }}>
              <Flex
                as={Link}
                to={`/networks/${slug}/lists/create`}
                alignItems="center"
                sx={{
                  gap: '8px',
                  color: 'text.main',
                  py: '4px',
                  '&:hover': { color: 'text.dark' },
                  textDecoration: 'none',
                }}
                onClick={handleCreateListClick}
              >
                <Box as={Plus} size="16px" />
                <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                  Create list
                </Text>
              </Flex>
              <Flex
                alignItems="center"
                sx={{ gap: '8px', color: 'text.main', py: '4px', '&:hover': { color: 'text.dark' } }}
                onClick={openCreateFolderModal}
              >
                <Box as={FolderPlus} size="16px" />
                <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                  Create folder
                </Text>
              </Flex>
            </Card>
          </Dropdown>
        </Box>
      </Flex>
      {openCreateFolder && (
        <FolderNameModal
          title="Create folder"
          label="New folder name"
          confirmText="Create folder"
          isLoading={creatingFolder}
          onCancel={handleCancelCreateFolder}
          onSubmit={handleSubmitCreateFolder}
        />
      )}
      <Flex
        flexDirection="column"
        sx={{
          opacity: isOpen ? 1 : 0,
          height: isOpen ? '100%' : 0,
          overflow: 'hidden',
          transition: 'all 0.1s ease-in-out',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        <Box sx={{ pb: '8px', flex: '1 1 auto', minHeight: 0 }}>
          <ListsNavTree slug={slug} />
        </Box>
      </Flex>
    </Box>
  );
};

ListLinks.propTypes = {
  slug: PropTypes.string.isRequired,
};

export default ListLinks;
